import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { FormListItem } from '@/services/formsApi';
import { useEffect, useState } from 'react';

export type CreateIndividualMode = 'blank' | 'copy';

export interface CopyFromPriorYearDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Current tax year being created for. */
  selectedYear: number;
  /** Single call: list prior Individual forms (taxYear < selectedYear). */
  listPriorForms: (beforeTaxYear: number) => Promise<FormListItem[]>;
  onCreate: (options: {
    mode: CreateIndividualMode;
    copyPersonalInfoFromFormId?: string;
  }) => void | Promise<void>;
  loading?: boolean;
}

export function CopyFromPriorYearDialog({
  open,
  onOpenChange,
  selectedYear,
  listPriorForms,
  onCreate,
  loading = false,
}: CopyFromPriorYearDialogProps) {
  const [mode, setMode] = useState<CreateIndividualMode>('blank');
  const [priorForms, setPriorForms] = useState<FormListItem[]>([]);
  const [selectedFormId, setSelectedFormId] = useState<string>('');
  const [loadingPriors, setLoadingPriors] = useState(false);

  useEffect(() => {
    if (!open) return;

    setMode('blank');
    setSelectedFormId('');
    setPriorForms([]);

    let cancelled = false;
    setLoadingPriors(true);

    (async () => {
      try {
        const forms = await listPriorForms(selectedYear);
        if (cancelled) return;
        setPriorForms(forms);
        if (forms.length > 0) {
          setSelectedFormId(forms[0].id);
        }
      } catch {
        if (!cancelled) setPriorForms([]);
      } finally {
        if (!cancelled) setLoadingPriors(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, selectedYear, listPriorForms]);

  const hasPriorForms = priorForms.length > 0;
  const copyDisabled = !hasPriorForms || loadingPriors;

  const handleSubmit = async () => {
    if (mode === 'copy') {
      if (!selectedFormId) return;
      await onCreate({ mode: 'copy', copyPersonalInfoFromFormId: selectedFormId });
    } else {
      await onCreate({ mode: 'blank' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New individual form</DialogTitle>
          <DialogDescription>
            Start a blank {selectedYear} form, or copy taxpayer, spouse, and dependent
            identity fields from a previous year.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="space-y-3">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="radio"
                name="individual-create-mode"
                className="mt-1"
                checked={mode === 'blank'}
                onChange={() => setMode('blank')}
                disabled={loading}
              />
              <span>
                <span className="font-medium text-sm">Start blank</span>
                <span className="block text-sm text-muted-foreground">
                  Create an empty form and fill personal information later.
                </span>
              </span>
            </label>

            <label
              className={`flex items-start gap-3 ${
                copyDisabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
              }`}
            >
              <input
                type="radio"
                name="individual-create-mode"
                className="mt-1"
                checked={mode === 'copy'}
                onChange={() => setMode('copy')}
                disabled={loading || copyDisabled}
              />
              <span>
                <span className="font-medium text-sm">
                  Copy personal info from a previous year
                </span>
                <span className="block text-sm text-muted-foreground">
                  {loadingPriors
                    ? 'Looking for prior Individual forms…'
                    : hasPriorForms
                      ? 'Copies names, DOB, SSN, visa, first date of entry, and occupation.'
                      : 'No prior Individual forms found for earlier tax years.'}
                </span>
              </span>
            </label>
          </div>

          {mode === 'copy' && hasPriorForms && (
            <div className="space-y-2 pl-7">
              <Label htmlFor="copy-from-prior-year">Source year</Label>
              <Select
                value={selectedFormId}
                onValueChange={setSelectedFormId}
                disabled={loading}
              >
                <SelectTrigger id="copy-from-prior-year">
                  <SelectValue placeholder="Select a prior year form" />
                </SelectTrigger>
                <SelectContent>
                  {priorForms.map((form) => (
                    <SelectItem key={form.id} value={form.id}>
                      Tax year {form.taxYear}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              loading ||
              loadingPriors ||
              (mode === 'copy' && (!hasPriorForms || !selectedFormId))
            }
          >
            {loading ? 'Creating…' : 'Create'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
